// Adaptación de tu App.tsx con protección de carga de traducciones

import '@refinedev/antd/dist/reset.css';
import './App.scss';

import { useNotificationProvider } from '@refinedev/antd';
import { I18nProvider, Refine } from '@refinedev/core';
import { RefineKbar, RefineKbarProvider } from '@refinedev/kbar';
import routerBindings, {
  DocumentTitleHandler,
  UnsavedChangesNotifier,
} from '@refinedev/react-router';
import { App as AntdApp } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter } from 'react-router';

import { authProvider } from './authProvider';
import { axiosConfig } from './axiosInstance';
import ProtectedBreadcrumb from './components/protected/ProtectedBreadcrumb';
import { API_URL, APP_BASENAME } from './constants/urls';
import { ColorModeContextProvider } from './contexts/ColorModeContext';
import { CountryProvider } from './contexts/CountryContext';
import { PermissionProvider } from './contexts/PermissionContext';
import { UIProvider } from './contexts/UiContext';
import { UserProvider } from './contexts/UserContext';
import { dataProvider } from './dataProvider';
import AppRoutes from './pages/AppRoutes';
import { RepositoriesProvider } from './repositories/RepositoriesContext';
import { REFINE_RESOURCES } from './Resources';

const App = () => {
  const axiosInstance = useMemo(() => axiosConfig(), []);
  const { t, i18n } = useTranslation();
  const i18nProvider = useMemo<I18nProvider>(
    () => ({
      translate: (key, options) => {
        const result = t(key, options);

        return typeof result === 'string' ? result : '';
      },
      changeLocale: lang => i18n.changeLanguage(lang),
      getLocale: () => i18n.language,
    }),
    [t, i18n],
  );

  return (
    <BrowserRouter basename={APP_BASENAME}>
      <RefineKbarProvider>
        <UIProvider>
          <RepositoriesProvider axiosInstance={axiosInstance}>
            <ColorModeContextProvider>
              <AntdApp className="App">
                <Refine
                  i18nProvider={i18nProvider}
                  dataProvider={dataProvider(API_URL, axiosInstance)}
                  notificationProvider={useNotificationProvider}
                  authProvider={authProvider(axiosInstance)}
                  routerProvider={routerBindings}
                  resources={REFINE_RESOURCES}
                  options={{
                    reactQuery: {
                      clientConfig: {
                        defaultOptions: {
                          queries: {
                            // Evita refetch automático al recuperar conexión/volver al foco
                            refetchOnReconnect: false,
                            refetchOnWindowFocus: false,
                          },
                        },
                      },
                    },
                    breadcrumb: <ProtectedBreadcrumb unauthorizedMode="hide" />,
                    redirect: { afterEdit: 'show', afterCreate: 'show' },
                    syncWithLocation: true,
                    warnWhenUnsavedChanges: true,
                    projectId: 'zWM3eU-WPujdu-NFzMfD',
                  }}>
                  <UserProvider>
                    <CountryProvider>
                      <PermissionProvider>
                        <AppRoutes />
                        <RefineKbar />
                        <UnsavedChangesNotifier />
                        <DocumentTitleHandler
                          handler={({ resource }) => {
                            const resourceLabel = resource?.meta?.label ?? resource?.name;

                            if (!resourceLabel) {
                              return 'Acronex operaciones';
                            }

                            return `${resourceLabel} | Acronex`;
                          }}
                        />
                      </PermissionProvider>
                    </CountryProvider>
                  </UserProvider>
                </Refine>
              </AntdApp>
            </ColorModeContextProvider>
          </RepositoriesProvider>
        </UIProvider>
      </RefineKbarProvider>
    </BrowserRouter>
  );
};

export default App;
